import { isTreatmentQuestionVisible } from "./questionVisibility";
import {
  categoryHasMultipleRequiredCards,
  isAutoIncludedRequiredCompanion,
  requiredSupplyGroups,
  requiredSupplyOptionFor,
  normalizeProductSelection,
} from "./checkoutContract";
import { isCommonSectionQuestion } from "./authorityProgress";
import {
  isCanonicalConsentQuestion,
  isInlineProgramConsentQuestion,
} from "./consentQuestionClassification";
import { libraryConsentAuthorityAnswer } from "./consentAuthorityContract";
import {
  canonicalConsentAnswer,
  canonicalProgramQuestionAnswer,
} from "./programAnswerCheckpoint";

export {
  buildBoundaryAnswerCheckpoint,
  buildProgramAnswerCheckpoint,
  buildProgramAnswerContext,
  canonicalConsentAnswer,
  canonicalProgramQuestionAnswer,
  checkpointProgramIdentity,
  hasCanonicalProgramDisqualifier,
  requiresProgramAnswerCheckpoint,
  recoverHiddenAnswerCheckpoint,
} from "./programAnswerCheckpoint";

const answered = (responses, question) => {
  if (!question || !responses) return undefined;
  const identities = [
    question.id,
    question.source_id,
    question.release_node_id,
  ].filter(Boolean);
  const responseIdentity = identities.find((identity) => (
    Object.prototype.hasOwnProperty.call(responses, identity)
  ));
  return responseIdentity === undefined
    ? undefined
    : responses[responseIdentity];
};
const sourceId = (question) => String(question.source_id || question.id);
const canonicalChoiceScalar = (value) => {
  if (!value || typeof value !== "object") return value;
  const key = ["value", "code", "option_id", "id"].find(
    (candidate) => value[candidate] !== undefined
      && value[candidate] !== null
      && value[candidate] !== "",
  );
  return key ? value[key] : "";
};
const answerScalar = canonicalChoiceScalar;

const answerBmi = (value) => {
  if (!value || typeof value !== "object") return value;
  if (value.bmi !== undefined && value.bmi !== null && value.bmi !== "") {
    return Number(value.bmi);
  }
  const feet = Number(value.feet);
  const inches = Number(value.inches);
  const weight = Number(value.weight);
  const totalInches = feet * 12 + inches;
  if (totalInches <= 0 || weight <= 0) return "";
  return Math.round(((weight / (totalInches * totalInches)) * 703) * 10) / 10;
};
const selectedValues = (value) => {
  const values = Array.isArray(value) ? value : value == null ? [] : [value];
  return values.map((item) => String(
    typeof item === "object"
      ? item.program_id || item.option_id || item.value || item.id || item.product_id
      : item
  ));
};

const optionIdentifiers = (choice) => {
  if (!choice || typeof choice !== "object") return [choice];
  return [
    choice.option_id,
    choice.optionId,
    choice.value,
    choice.id,
    choice.product?.option_id,
  ].filter((value) => value !== undefined && value !== null && value !== "");
};

const locationField = (question) => {
  const mapping = String(question?.beluga_field_mapping || "").toLowerCase();
  const text = String(question?.question_text || question?.label || "").toLowerCase();
  const identity = `${mapping} ${text}`;
  if (/zip|postal/.test(identity)) return "zip_code";
  if (/country/.test(identity)) return "country";
  if (/city/.test(identity)) return "city";
  if (/state/.test(identity)) return "state";
  if (/apartment|suite|unit|address_?2|street_?2/.test(identity)) return "street_2";
  if (/street|address/.test(identity)) return "street_1";
  return null;
};

export const canonicalLocationAnswer = (question, value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const direct = {
    street_1: value.street_1 || value.street_address || value.address || value.address_line_1,
    street_2: value.street_2 || value.address_line_2,
    city: value.city,
    state: value.state,
    zip_code: value.zip_code || value.zip || value.postal_code,
    country: value.country || "US",
  };
  if (value.accepted_correction_fingerprint) {
    direct.accepted_correction_fingerprint = value.accepted_correction_fingerprint;
  }
  (question?.sub_questions || []).forEach((subQuestion) => {
    const field = locationField(subQuestion);
    if (
      field
      && Object.prototype.hasOwnProperty.call(value, subQuestion.id)
    ) {
      direct[field] = value[subQuestion.id];
    }
  });
  Object.keys(direct).forEach((key) => {
    direct[key] = String(direct[key] || "").trim();
  });
  return direct.street_1 && direct.zip_code ? direct : null;
};

export const applyVerifiedAddressCorrection = (
  questionnaire,
  responses,
  details,
) => {
  const suggested = details?.suggested_location;
  const fingerprint = details?.correction_fingerprint || suggested?.fingerprint;
  if (!suggested || !fingerprint) return null;
  const isShipping = details?.corrective_action?.code === "confirm_shipping_location";
  const questions = Array.isArray(questionnaire?.questions)
    ? questionnaire.questions
    : [];
  const question = questions.find((candidate) => (
    candidate.question_type === "shipping_address"
    && (isShipping
      ? candidate.location_role === "shipping"
      : candidate.location_role !== "shipping")
  ));
  if (!question) return null;
  const corrected = {
    ...(responses?.[question.id] || {}),
    street_1: suggested.street_1,
    street_2: suggested.street_2 || "",
    city: suggested.city,
    state: suggested.state,
    zip_code: suggested.zip_code,
    country: suggested.country || "US",
    accepted_correction_fingerprint: fingerprint,
  };
  (question.sub_questions || []).forEach((subQuestion) => {
    const field = locationField(subQuestion);
    if (field) corrected[subQuestion.id] = corrected[field];
  });
  return {
    questionId: question.id,
    responses: { ...responses, [question.id]: corrected },
  };
};

const matchedQuestion = (questions, stage) => questions.find(
  (question) => question.question_type === "matched_program_selection"
    && question.matching_config?.stage === stage
);

export const buildAuthorityPayload = (questionnaire, responses) => {
  const rawQuestions = (
    Array.isArray(questionnaire?.questions)
      ? questionnaire.questions
      : []
  );
  const groupedProductResponseEntry = Object.entries(responses || {}).find(
    ([responseId, value]) => (
      responseId.startsWith("grouped-product-selection-")
      && value !== undefined
      && value !== null
      && value !== ""
    ),
  );
  const hasAnsweredValue = (question) => {
    const value = answered(responses, question);
    return value !== undefined && value !== null && value !== "";
  };
  const questions = rawQuestions.filter((question) => (
    isTreatmentQuestionVisible(question, questionnaire, responses)
    || (groupedProductResponseEntry
      && question.question_type === "product_selection")
    || question.question_type === "product_selection"
    // Keep boundary inputs that were just entered even if a stale conditional
    // projection temporarily hides the checkout question. Otherwise the
    // final runtime transition receives an empty checkout payload and rejects
    // a valid address as missing. Lab checkout is subject to the same
    // projection timing as shipping, particularly after a Custom Program
    // selection changes the recommended-program context.
    || (
      ["shipping_address", "program_lab_checkout"].includes(
        question.question_type,
      )
      && hasAnsweredValue(question)
    )
  ));
  const potential = matchedQuestion(questions, "potential");
  const recommendations = matchedQuestion(questions, "recommendations");
  const routing = {};
  const common = {};
  const programs = {};
  const consents = {};
  const selections = [];
  const labSelections = [];
  let labCheckout = null;
  let serviceState = "";
  const profile = {};
  let location = null;
  let shippingLocation = null;
  let shippingQuestion = null;
  // Product-selection questions are checkout inputs, not conditional
  // screening answers. A multi-product treatment can contain several of
  // them, and visibility evaluation can hide later questions after the
  // response has been normalized. Always merge every published product
  // question so persisted source responses cannot be dropped.
  const productQuestions = rawQuestions.filter(
    (question) => question.question_type === "product_selection",
  );

  questions.forEach((question) => {
    const rawValue = answered(responses, question);
    const value = rawValue;
    if (
      (value === undefined || value === null || value === "")
      && question.question_type !== "product_selection"
    ) return;
    const key = sourceId(question);
    if (question.question_type === "shipping_address") {
      if (question.location_role === "shipping") {
        shippingQuestion = question;
        shippingLocation = canonicalLocationAnswer(question, value) || shippingLocation;
      } else {
        // No role is the version-1 compatibility shape. It established the
        // canonical service location before the explicit v2 split existed.
        location = canonicalLocationAnswer(question, value) || location;
      }
      // A Shipping Address authored inside a common Section remains part of
      // that Section's common answer contract. Keep the location projection
      // above as well because the runtime uses it for checkout authority.
      if (isCommonSectionQuestion(question)) {
        common[key] = value;
      }
      return;
    }
    if (["date_of_birth", "sex"].includes(question.beluga_field_mapping)) {
      profile[question.beluga_field_mapping] = answerScalar(value);
    }
    if (question.routing_profile) {
      profile.bmi = answerBmi(value);
      routing.bmi = profile.bmi;
    }
    if (
      question.question_type === "state_routing"
      || question.beluga_field_mapping === "state"
      || key === "service_state"
    ) {
      const state = answerScalar(value);
      serviceState = String(state).trim().toUpperCase();
      profile.service_state = serviceState;
      routing[key] = state;
      // Section-owned Service Area Checks are retained in common_sections
      // after deduplication. Submit the answer to both contracts: routing
      // owns the visit service state, while common answers satisfy the
      // retained Section field.
      if (isCommonSectionQuestion(question)) {
        common[key] = value;
      }
    } else if (
      question.scope === "common"
      && (
        question.question_type !== "consent"
        || isCommonSectionQuestion(question)
      )
    ) {
      common[key] = question.question_type === "consent"
        ? canonicalConsentAnswer(value)
        : value;
    } else if (
      question.program_id
      && (
        question.question_type === "consent"
          ? isInlineProgramConsentQuestion(question)
          : !["product_selection", "treatment_screening_intro"].includes(
              question.question_type,
            )
      )
    ) {
      const programId = String(question.program_id);
      programs[programId] = {
        ...(programs[programId] || {}),
        [key]: canonicalProgramQuestionAnswer(question, value),
      };
    } else if (question.question_type === "consent") {
      const nodeId = String(question.release_node_id || key);
      consents[nodeId] = libraryConsentAuthorityAnswer(value);
    } else if (question.question_type === "product_selection") {
      // Product responses are merged once using the union of all product
      // questions; do not repeat the merged set for every source question.
      if (question.id !== productQuestions[0]?.id) return;
      // The UI may have stored per-question responses, a synthetic grouped
      // response from an older bundle, or both. Merge every source so a stale
      // grouped value cannot hide the authoritative selections.
      const choices = productQuestions.flatMap((productQuestion) => (
        productQuestion.answer_choices
        || productQuestion.checkout_config?.products
        || []
      ));
      const releasedOptionIds = new Set(
        choices
          .map((choice) => String(choice?.option_id || ""))
          .filter(Boolean),
      );
      const responseValues = productQuestions
        .map((productQuestion) => answered(responses, productQuestion))
        .filter((productResponse) => (
          productResponse !== undefined
          && productResponse !== null
          && productResponse !== ""
        ));
      if (groupedProductResponseEntry) {
        responseValues.push(groupedProductResponseEntry[1]);
      }
      // Runtime and UI projections can legitimately use different stable
      // question identities. Product authority is the immutable released
      // option ID, so recover candidates by that ID and still reject every
      // value that is not present in the published Product choices.
      const releasedCandidates = Object.values(responses || {}).flatMap(
        (responseValue) => (
          Array.isArray(responseValue) ? responseValue : [responseValue]
        ),
      ).filter((responseValue) => (
        responseValue
        && typeof responseValue === "object"
        && releasedOptionIds.has(String(responseValue.option_id || ""))
      ));
      if (releasedCandidates.length) {
        responseValues.push(releasedCandidates);
      }
      const selectedItems = responseValues.flatMap((productResponse) => (
        Array.isArray(productResponse) ? productResponse : [productResponse]
      ));
      const seenOptionIds = new Set();
      const requiredSupplyOptionIds = new Set(
        requiredSupplyGroups(choices).flatMap((group) => group
          .filter((choice) => choice?.product_role === "required_companion")
          .map((choice) => String(choice?.option_id || ""))
          .filter(Boolean)),
      );
      selectedItems.forEach((selectedItem) => {
        const normalized = normalizeProductSelection(selectedItem, choices);
        const authoritativeOptionId = normalized?.option_id;
        if (!authoritativeOptionId || seenOptionIds.has(String(authoritativeOptionId))) return;
        seenOptionIds.add(String(authoritativeOptionId));
        if (normalized) selections.push(normalized);
      });

      // Required companions are rendered as "Included automatically" and
      // may not have made it into React state yet when Continue is clicked.
      // They are part of the immutable checkout selection, so include them
      // from the published questionnaire as a final safety net.
      choices
        .filter((choice) => (
          isAutoIncludedRequiredCompanion(choice, choices)
          && !requiredSupplyOptionIds.has(String(choice?.option_id || ""))
        ))
        .forEach((choice) => {
          const normalized = normalizeProductSelection(choice, choices);
          const authoritativeOptionId = normalized?.option_id;
          if (!authoritativeOptionId || seenOptionIds.has(String(authoritativeOptionId))) return;
          seenOptionIds.add(String(authoritativeOptionId));
          if (normalized) selections.push(normalized);
        });

      // A grouped supply can mix required and optional rows. Always include
      // exactly one required duration, preferring an already-selected required
      // variant and otherwise using authored order. This fallback is keyed by
      // immutable option_id and also covers selections spread across source
      // checkout questions before React has committed the latest state.
      requiredSupplyGroups(choices).forEach((group) => {
        const requiredMembers = group.filter(
          (member) => member?.product_role === "required_companion",
        );
        const anchor = requiredMembers[0] || group[0];
        if (anchor && categoryHasMultipleRequiredCards(anchor, choices)) {
          return;
        }
        const choice = requiredSupplyOptionFor(group, selections);
        const normalized = normalizeProductSelection(choice, choices);
        const authoritativeOptionId = normalized?.option_id;
        if (!authoritativeOptionId || seenOptionIds.has(String(authoritativeOptionId))) return;
        seenOptionIds.add(String(authoritativeOptionId));
        if (normalized) selections.push(normalized);
      });
    } else if (question.question_type === "program_lab_checkout") {
      if (
        value
        && typeof value === "object"
        && Object.keys(value).length > 0
        && (value.address || value.labs || value.consents)
      ) {
        labCheckout = value;
        Object.entries(value?.labs || {}).forEach(
          ([requirementId, selected]) => {
            if (
              selected === true
              || (selected && typeof selected === "object" && selected.selected === true)
            ) {
              labSelections.push(String(requirementId));
            }
          },
        );
      }
    } else if (question.scope === "routing") {
      routing[key] = value;
    } else if (
      ["date_of_birth", "sex"].includes(question.beluga_field_mapping)
    ) {
      routing[key] = answerScalar(value);
    }
  });

  const potentialPrograms = selectedValues(answered(responses, potential));
  const recommendedPrograms = selectedValues(answered(responses, recommendations));
  const selectedProgramIds = recommendedPrograms.length
    ? recommendedPrograms
    : potentialPrograms;
  const policies = shippingQuestion?.shipping_destination_policies || {};
  const separateAllowed = selectedProgramIds.length > 0 && selectedProgramIds.every(
    (programId) => policies[String(programId)] === "separate_verified_allowed",
  );
  const locationFingerprintMaterial = (value) => JSON.stringify([
    value?.street_1?.toLowerCase(),
    value?.street_2?.toLowerCase(),
    value?.city?.toLowerCase(),
    value?.state?.toUpperCase(),
    value?.zip_code,
    value?.country?.toUpperCase(),
  ]);
  const useServiceLocation = !separateAllowed
    || !shippingLocation
    || locationFingerprintMaterial(shippingLocation) === locationFingerprintMaterial(location);
  const stateOnlyCareContract = Number(
    questionnaire?.location_contract_version || 0,
  ) >= 3;

  return {
    serviceState,
    location,
    ...(shippingQuestion ? {
      shipping: stateOnlyCareContract
        ? {
            use_service_location: false,
            ...(shippingLocation ? { location: shippingLocation } : {}),
          }
        : {
            use_service_location: useServiceLocation,
            ...(!useServiceLocation ? { location: shippingLocation } : {}),
          },
    } : {}),
    profile,
    routing,
    selectedPrograms: potentialPrograms,
    common,
    programs,
    consents,
    recommendations: recommendedPrograms,
    selections,
    labSelections: [...new Set(labSelections)],
    labCheckout,
  };
};

export const normalizePaymentMethod = (payment) => {
  const gateway = String(payment?.gateway || "").toLowerCase();
  const data = payment?.payment_data;
  if (!gateway || !data) return null;
  if (!["stripe", "nmi", "authorize_net", "authorizenet"].includes(gateway)) return null;
  return {
    gateway: gateway === "authorizenet" ? "authorize_net" : gateway,
    payment_data: typeof data === "object" ? { ...data } : { token: String(data) },
  };
};

export const paymentMethodToken = (payment) => {
  const normalized = normalizePaymentMethod(payment);
  const data = normalized?.payment_data;
  if (normalized?.gateway === "stripe") return data?.payment_method_id || data?.id || data?.token;
  if (normalized?.gateway === "nmi") return data?.saved_payment_method_id || data?.payment_token || data?.token;
  if (normalized?.gateway === "authorize_net") {
    if (data?.saved_payment_method_id) return String(data.saved_payment_method_id);
    return data ? JSON.stringify(data) : null;
  }
  return null;
};
